import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { FamilyRelation, FamilyRelationRequest } from './familyRelationTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const familyRelationsApi = {
  /** Soft-deletes every relation at once; resolves with how many. Members already recorded stay. */
  clear: () => api<number>('/api/family-relations/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<FamilyRelation>>(`/api/family-relations${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<FamilyRelation>(`/api/family-relations/${id}`, { signal }),

  /**
   * The ACTIVE relations, in the form's order, for the family form's picker.
   *
   * Deleted rows are left out: the form offers what can be recorded now. A member already
   * recorded at a deleted relation keeps it, and the panel still names it.
   */
  options: (signal?: AbortSignal) =>
    api<FamilyRelation[]>('/api/family-relations/options', { signal }),

  /**
   * Adds a relation, or returns the active one that already carries the name.
   *
   * **Idempotent on the name**, because the family form's own picker calls this: a reader who
   * needs "Guardian" should not have to leave for a settings page and come back to a form they
   * have to fill again, and two of them doing it at once must get one row rather than an error
   * neither caused.
   */
  create: (request: FamilyRelationRequest) =>
    api<FamilyRelation>('/api/family-relations', { method: 'POST', body: request }),

  update: (id: string, request: FamilyRelationRequest) =>
    api<FamilyRelation>(`/api/family-relations/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the choice leaves the form, the members stay on the records. */
  deactivate: (id: string) => api<void>(`/api/family-relations/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/family-relations/${id}/restore`, { method: 'POST' }),
};
